<template>
  <aside class="list-of-figures-page">
    <div>
      <DocumentPageTitle
        :show-word-count="false"
        title="List of figures"
      />

      <p class="light-chrome-banner">
        This list updates automatically as you add figures. Please ensure each image is named correctly;
        you can edit these names at any time. Page numbers will only appear once a pdf has been generated.
      </p>

      <AppTable>
        <AppTableBody
          :loading="planStore.loading"
          :colspan="2"
          :no-results="planContentKey.length === 0"
          no-results-text="No figures in the document"
        >
          <tr
            v-for="(figure, index) in planContentKey"
            :key="index"
          >
            <td class="!border-r-0 w-3/4">
              Figure {{ index + 1 }}: {{ figure.caption || 'No caption provided' }}
            </td>

            <td>
              <img
                :src="figure.src"
                class="w-auto h-10 ml-auto"
              >
            </td>
          </tr>
        </AppTableBody>
      </AppTable>
    </div>
  </aside>
</template>

<script setup>
import { AppTable, AppTableBody } from '@/components/tables';
import { DocumentPageTitle } from '@/components/plan-document/components';

import { usePlanStore } from '@/store';
import { computed } from 'vue';

const planStore = usePlanStore();

// Editor IDs in document page order, matching the PDF preview section traversal.
// Update this list if pages or sections are added, removed, or reordered.
const EDITOR_ORDER = [
  'message-from-the-mayor',
  'message-from-the-municipal-manager',
  'who-are-we',
  'what-are-the-issues-we-are-facing',
  'what-opportunities-do-we-offer',
  'what-are-we-doing-to-improve-service-delivery-over-the-next-5-years',
  'priorities-of-council',
  'what-can-you-expect-from-us',
  'how-will-our-progress-be-measured',
  'how-was-this-idp-developed',
  'powers-and-functions',
  'vision',
  'mission',
  'values',
  'goals',
  'objectives',
  'overview-of-the-municipality-editor',
  'demographic-profile-editor',
  'socio-economic-profile-editor',
  'economic-profile-editor',
  'service-delivery-and-infrastructure-editor',
  'finance-and-governance-editor',
  'environmental-context-editor',
  'overview-of-community-needs-and-ward-priorities-editor',
  'governance',
  'municipal-structure',
  'skills-requirements',
  'strengths-and-weaknesses',
  'employment-equity',
  'spatial-economic-and-development-rationale',
  'desired-spatial-outcome',
  'spatial-strategies',
  'overview-of-implementation-plan-editor',
  'programmes-and-projects-of-other-organs-of-state-editor',
  'local-economic-development-editor',
  'disaster-management-plan-editor',
  'climate-change-response-implementation-plan-editor',
  'financial-strategy-editor',
  'comments-to-the-financial-plan-editor',
  'introduction',
];

const planContentKey = computed(() => {
  const figures = planStore.content?.figures || [];

  // indexOf returns the position in EDITOR_ORDER, or -1 if not found.
  // -1 is replaced with Infinity so unknown editors always sort to the bottom.
  // orderA - orderB: negative = a first, positive = b first, 0 = same editor (preserves existing DOM order within that editor).
  return [...figures].sort((a, b) => {
    const indexA = EDITOR_ORDER.indexOf(a.editorId);
    const indexB = EDITOR_ORDER.indexOf(b.editorId);
    const orderA = indexA === -1 ? Infinity : indexA;
    const orderB = indexB === -1 ? Infinity : indexB;
    return orderA - orderB;
  });
});
</script>
